import React from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import { Col, Row } from "react-bootstrap";
import Button from "src/components/Button/Button";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";

const normalizeItems = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      if (typeof item === "string") {
        return { text: item, icon: "•" };
      }

      if (item && typeof item === "object") {
        return {
          icon: item.icon || "•",
          title: item.title || "",
          text: item.text || "",
          subText: item.subText || "",
        };
      }

      return null;
    })
    .filter(Boolean);
};

const PromoInfoImageGrid = ({ section }) => {
  if (!section) {
    return null;
  }

  const {
    id,
    heading,
    stepsTitle,
    steps = [],
    prizesTitle,
    prizes = [],
    disclaimer,
    cta,
    image,
    imageAlt,
    imagePosition = "right",
  } = section;

  const normalizedSteps = normalizeItems(steps);
  const normalizedPrizes = normalizeItems(prizes);

  const hasLeftContent =
    Boolean(heading) ||
    Boolean(stepsTitle) ||
    normalizedSteps.length > 0 ||
    Boolean(prizesTitle) ||
    normalizedPrizes.length > 0 ||
    Boolean(cta?.label);

  if (!hasLeftContent && !image) {
    return null;
  }

  const imageFirst = imagePosition === "left";
  const imageColumn = image && (
    <Col xs={12} lg={6}>
      <ImageWrap>
        <ResponsiveImage src={image} alt={imageAlt || "Promotion visual"} />
      </ImageWrap>
    </Col>
  );

  const contentColumn = (
    <Col xs={12} lg={6}>
      <InfoCard>
        {heading && (
          <SectionHeading>
            <ReactMarkdown>{heading}</ReactMarkdown>
          </SectionHeading>
        )}

        {stepsTitle && (
          <BlockTitle>
            <ReactMarkdown>{stepsTitle}</ReactMarkdown>
          </BlockTitle>
        )}
        {normalizedSteps.length > 0 && (
          <StyledList>
            {normalizedSteps.map((item, index) => (
              <li key={`step-${index}`}>
                <ListIcon>{item.icon}</ListIcon>
                <ListContent>
                  {item.title && (
                    <ListTitle>
                      <ReactMarkdown>{item.title}</ReactMarkdown>
                    </ListTitle>
                  )}
                  {item.text && <ReactMarkdown>{item.text}</ReactMarkdown>}
                  {item.subText && <ListSubText>{item.subText}</ListSubText>}
                </ListContent>
              </li>
            ))}
          </StyledList>
        )}

        {prizesTitle && (
          <BlockTitle className="mt-4">
            <ReactMarkdown>{prizesTitle}</ReactMarkdown>
          </BlockTitle>
        )}
        {normalizedPrizes.length > 0 && (
          <StyledList>
            {normalizedPrizes.map((item, index) => (
              <li key={`prize-${index}`}>
                <ListIcon>{item.icon}</ListIcon>
                <ListContent>
                  {item.title && (
                    <ListTitle>
                      <ReactMarkdown>{item.title}</ReactMarkdown>
                    </ListTitle>
                  )}
                  {item.text && <ReactMarkdown>{item.text}</ReactMarkdown>}
                  {item.subText && <ListSubText>{item.subText}</ListSubText>}
                </ListContent>
              </li>
            ))}
          </StyledList>
        )}

        {cta?.label && cta?.url && (
          <CtaWrap>
            <Button
              href={cta.url}
              label={cta.label}
              bgColor="#f4f4f4"
              color="#d1879a"
              hoverBgColor="#f7d5dd"
              hoverColor="#bb6d82"
              size="medium"
              width="wide"
              className="promo-info-grid-cta"
            />
          </CtaWrap>
        )}
      </InfoCard>
    </Col>
  );

  return (
    <>
      <GridRow id={id || undefined} className="g-3 g-lg-4 align-items-stretch">
        {imageFirst ? imageColumn : contentColumn}
        {imageFirst ? contentColumn : imageColumn}
      </GridRow>

      {disclaimer && (
        <GridDisclaimer>
          <ReactMarkdown>{disclaimer}</ReactMarkdown>
        </GridDisclaimer>
      )}
    </>
  );
};

const GridRow = styled(Row)`
  max-width: 1200px;
  margin: 0 auto;
`;

const GridDisclaimer = styled.div`
  max-width: 1200px;
  margin: 0.85rem auto 0;
  text-align: center;
  color: var(--lightblack);
  font-style: italic;
  font-size: 12px !important;
  line-height: 1.4;
  opacity: 0.95;

  p {
    margin: 0;
    font-size: 12px !important;
    line-height: 1.4;
  }

  em,
  strong,
  span {
    font-size: 12px !important;
  }
`;

const InfoCard = styled.div`
  height: 100%;
  background: #cd8d9b;
  border-radius: 10px;
  padding: 2rem 1.7rem 1rem 1.7rem;
  color: var(--white);

  p {
    margin: 0;
  }
`;

const SectionHeading = styled.div`
  margin-bottom: 1.1rem;
  font-weight: 700;
  color: var(--lightblack);

  p {
    margin: 0;
  }
`;

const BlockTitle = styled.div`
  margin-bottom: 0.95rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--lightblack);
  h3,
  h5,
  p,
  strong {
    margin: 0;
    color: rgb(101, 76, 80) !important;
    line-height: inherit;
    font-weight: inherit;
  }

`;

const StyledList = styled.ul`
  list-style: none;
  margin: 0 0 0.4rem;
  padding: 0;

  li {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 0.5rem;
    align-items: flex-start;
    margin-bottom: 0.75rem;
    line-height: 1.25;
    color: #fff7fb;
  }

  p {
    margin: 0;
  }
`;

const ListIcon = styled.span`
  line-height: 1.1;
`;

const ListContent = styled.div`
  p {
    margin: 0;
  }
`;

const ListTitle = styled.div`
  font-weight: 700;
`;

const ListSubText = styled.p`
  margin: 0.15rem 0 0;
  padding-left: 2.1rem;

  @media (max-width: 768px) {
    padding-left: 1.6rem;
  }
`;

const CtaWrap = styled.div`
  text-align: center;
`;

const ImageWrap = styled.div`
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid rgba(0, 0, 0, 0.12);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export default PromoInfoImageGrid;
